import * as React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/pro-duotone-svg-icons/faFilter'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { styled } from '@mui/material/styles'
import { CompactButton } from './CompactButton'
import { buttonColorMap, ButtonColor } from './buttonColorMap'
import type { ButtonProps } from '@mui/material/Button'

interface ActiveCompactButtonProps
    extends React.ComponentProps<typeof CompactButton> {
    active?: boolean
    activeColor?: ButtonColor
}

const ActiveCompactButton = styled(CompactButton, {
    shouldForwardProp: (prop) => prop !== 'active' && prop !== 'activeColor',
})<ActiveCompactButtonProps>(({ active, activeColor = 'primary' }) => {
    const palette =
        buttonColorMap[activeColor as keyof typeof buttonColorMap] ??
        buttonColorMap.primary
    return active
        ? {
              backgroundColor: palette.active,
              color: palette.text,
              borderColor: palette.border,
              boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
              '&:hover': {
                  backgroundColor: palette.active,
                  color: palette.text,
              },
          }
        : {}
})

export type FilterToggleColorProp = Exclude<ButtonColor, 'light'>

export type FilterToggleButtonProps = Omit<
    ButtonProps,
    'children' | 'color' | 'variant'
> & {
    active?: boolean
    color?: FilterToggleColorProp
    dense?: boolean
    invert?: boolean
    icon?: IconDefinition
    variant?: 'outlined' | 'contained'
}

export function FilterToggleButton({
    active = false,
    color = 'primary',
    dense = true,
    icon = faFilter,
    variant = 'outlined',
    sx,
    ...rest
}: FilterToggleButtonProps) {
    return (
        <ActiveCompactButton
            dense={dense}
            color={color as ButtonProps['color']}
            variant={variant}
            active={active}
            activeColor={color}
            aria-pressed={active}
            {...rest}
            sx={sx}
        >
            <FontAwesomeIcon icon={icon} />
        </ActiveCompactButton>
    )
}
